// NoticeUploadDialog.js
import React, { useState, useCallback, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    makeStyles
} from '@material-ui/core';
import { useDropzone } from 'react-dropzone';
import { useIntl } from 'react-intl';

const useStyles = makeStyles((theme) => ({
    dropzone: {
        border: '2px dashed #ccc',
        padding: theme.spacing(2),
        textAlign: 'center',
        cursor: 'pointer',
        marginBottom: theme.spacing(2),
    },
    preview: {
        width: '100%',
        maxHeight: '200px',
        objectFit: 'cover',
        marginTop: theme.spacing(2),
    },
    error: {
        color: theme.palette.error.main,
        marginTop: theme.spacing(1),
    },
    guidelines: {
        marginBottom: theme.spacing(2),
    },
}));

function NoticeUploadDialog({ open, onClose, onUpload }) {
    const classes = useStyles();
    const intl = useIntl();
    const [file, setFile] = useState(null);
    const [url, setUrl] = useState('');
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState('');

    const onDrop = useCallback((acceptedFiles) => {
        const selectedFile = acceptedFiles[0];
        if (selectedFile) {
            const img = new Image();
            const objectUrl = URL.createObjectURL(selectedFile);
            img.src = objectUrl;
            img.onload = () => {
                const { width, height } = img;
                const aspectRatio = width / height;
                // Check if aspect ratio is approximately 5:1 (allowing a small margin)
                if (Math.abs(aspectRatio - 5) < 0.1) {
                    setFile(selectedFile);
                    setPreview(objectUrl);
                    setError('');
                } else {
                    setFile(null);
                    setPreview(null);
                    setError(intl.formatMessage({ id: "noticeUpload.error.aspectRatio" }));
                }
            };
            img.onerror = () => {
                setError(intl.formatMessage({ id: "noticeUpload.error.load" }));
            };
        }
    }, [intl]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: 'image/*',
        multiple: false,
    });

    const handleUpload = () => {
        if (file) {
            onUpload(file, url);
            // Reset the form after upload
            setFile(null);
            setPreview(null);
            setUrl('');
            setError('');
        }
    };

    // Clean up the object URL when component unmounts or file changes
    useEffect(() => {
        return () => {
            if (preview) {
                URL.revokeObjectURL(preview);
            }
        };
    }, [preview]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{intl.formatMessage({ id: "noticeUpload.title" })}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="textSecondary" className={classes.guidelines}>
                    {intl.formatMessage({ id: "noticeUpload.guidelines" })}
                </Typography>
                <Box {...getRootProps()} className={classes.dropzone}>
                    <input {...getInputProps()} />
                    {
                        isDragActive ?
                            <Typography>{intl.formatMessage({ id: "noticeUpload.dropHere" })}</Typography> :
                            <Typography>{intl.formatMessage({ id: "noticeUpload.dragDrop" })}</Typography>
                    }
                </Box>
                {error && <Typography className={classes.error}>{error}</Typography>}
                {file && (
                    <Typography variant="body2" gutterBottom>
                        {intl.formatMessage(
                            { id: "noticeUpload.selectedFile" },
                            { fileName: file.name }
                        )}
                    </Typography>
                )}
                {preview && (
                    <img src={preview} alt="Preview" className={classes.preview} />
                )}
                <TextField
                    fullWidth
                    label={intl.formatMessage({ id: "noticeUpload.urlLabel" })}
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    margin="normal"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    {intl.formatMessage({ id: "noticeUpload.cancel" })}
                </Button>
                <Button
                    onClick={handleUpload}
                    color="primary"
                    disabled={!file || !!error}
                >
                    {intl.formatMessage({ id: "noticeUpload.upload" })}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default NoticeUploadDialog;
